import moment from 'moment-timezone'
import { defaultTimeZoneId } from '@/config/flavor'
import { normalizeTimeZoneId, isValidTimeZoneId } from '@/constants/timeZones'

const { convertBrowserLocalToAccount: convertCore, timeZonesMatchByOffset } = require('./timezoneCore')

/** 默认 IANA 时区，与后端 TimezoneConstants.DEFAULT_TIME_ZONE_ID 一致 */
export const DEFAULT_TIME_ZONE = defaultTimeZoneId

/** 由 plugins/timezone.js 注入，避免 timezone ↔ store 循环依赖 */
let accountTimeZoneReader = null

export function bindAccountTimeZoneReader (reader) {
  accountTimeZoneReader = reader
}

/**
 * 读取账号 timeZoneId（IANA）。
 * 优先 Vuex（经 bindAccountTimeZoneReader），其次 localStorage。
 */
export function getAccountTimeZone () {
  if (accountTimeZoneReader) {
    const fromStore = accountTimeZoneReader()
    if (fromStore != null && fromStore !== '') {
      return normalizeTimeZoneId(fromStore)
    }
  }
  try {
    const raw = window.localStorage.getItem('timezone')
    if (raw != null && raw !== '') {
      const parsed = JSON.parse(raw)
      return normalizeTimeZoneId(parsed)
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_TIME_ZONE
}

/** 浏览器本地相对 UTC 的偏移（分钟），与 moment.utcOffset 一致 */
export function browserOffsetMinutes () {
  return -new Date().getTimezoneOffset()
}

/**
 * 本机 IANA 时区（优先 Intl；不可用时按当前 UTC 偏移回退）。
 */
export function getBrowserTimeZoneId () {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz != null && tz !== '' && isValidTimeZoneId(tz)) {
      return String(tz).trim()
    }
  } catch (e) {
    // ignore
  }
  const guessed = moment.tz.guess()
  if (guessed != null && guessed !== '' && isValidTimeZoneId(guessed)) {
    return String(guessed).trim()
  }
  return DEFAULT_TIME_ZONE
}

/**
 * 账号自定义时区与本机时区是否一致（按当前时刻 UTC 偏移比较）。
 */
export function isAccountTimeZoneMatchingBrowser (accountZoneId) {
  const accountZone = normalizeTimeZoneId(accountZoneId)
  const browserZone = getBrowserTimeZoneId()
  return timeZonesMatchByOffset(accountZone, browserZone)
}

/**
 * 浏览器本地墙钟 → 账号 IANA 时区墙钟（纯函数，便于单测）。
 */
export function convertBrowserLocalToAccount (
  value,
  accountZoneId,
  format = 'YYYY-MM-DD HH:mm:ss'
) {
  if (value == null || value === '') {
    return value
  }
  const zoneId = normalizeTimeZoneId(accountZoneId)
  return convertCore(value, zoneId, format)
}

/** 当前时刻（按账号 IANA 时区） */
export function accountNow () {
  return moment.tz(getAccountTimeZone())
}

/**
 * 账号时区下默认月份范围 [start, end]，格式 yyyy-MM。
 */
export function defaultMonthRange (monthsBack = 12) {
  const end = accountNow().clone().startOf('month')
  const start = end.clone().subtract(monthsBack - 1, 'months')
  return [start.format('YYYY-MM'), end.format('YYYY-MM')]
}

/**
 * ISO 偏移字符串，如 +08:00 / -05:00
 */
export function formatOffsetForIso (timeZoneId) {
  const zoneId = normalizeTimeZoneId(timeZoneId)
  return moment.tz(zoneId).format('Z')
}

/**
 * Element DatePicker 返回的浏览器本地墙钟 → 账号时区墙钟字符串（发 API 用）。
 */
export function browserLocalToAccountString (value, format = 'YYYY-MM-DD HH:mm:ss') {
  return convertBrowserLocalToAccount(value, getAccountTimeZone(), format)
}

/**
 * 展示 API 返回的时间字符串。
 * 后端列表接口已对时间做时区偏移，此处勿再转换，仅做空值处理。
 */
export function displayDateTime (value) {
  if (value == null || value === '' || value === '--') {
    return value == null ? '' : value
  }
  return value
}

export function rangeToAccountQuery (range, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!range || range.length < 2 || range[0] == null || range[1] == null) {
    return { startTime: '', endTime: '' }
  }
  return {
    startTime: browserLocalToAccountString(range[0], format),
    endTime: browserLocalToAccountString(range[1], format),
  }
}

export function dateRangeToAccountQuery (range) {
  if (!range || range.length < 2 || range[0] == null || range[1] == null) {
    return { startTime: '', endTime: '' }
  }
  return {
    startTime: browserLocalToAccountString(`${range[0]} 00:00:00`),
    endTime: browserLocalToAccountString(`${range[1]} 23:59:59`),
  }
}

export function dateRangeToAccountDates (range) {
  if (!range || range.length < 2 || range[0] == null || range[1] == null) {
    return { startDate: '', endDate: '' }
  }
  return {
    startDate: browserLocalToAccountString(range[0], 'YYYY-MM-DD'),
    endDate: browserLocalToAccountString(range[1], 'YYYY-MM-DD'),
  }
}

export default {
  bindAccountTimeZoneReader,
  getAccountTimeZone,
  getBrowserTimeZoneId,
  isAccountTimeZoneMatchingBrowser,
  browserOffsetMinutes,
  convertBrowserLocalToAccount,
  accountNow,
  defaultMonthRange,
  formatOffsetForIso,
  browserLocalToAccountString,
  rangeToAccountQuery,
  dateRangeToAccountQuery,
  dateRangeToAccountDates,
  displayDateTime,
  DEFAULT_TIME_ZONE,
}
